import {Box, Button, ButtonProps} from '@chakra-ui/react'
import {PatientTherapySequenceEventType} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {MouseEvent, useState} from 'react'
import {NoteBadge} from './note-badge'

// pharmacy note flags the pharmacist's validate step; prescription note flags the
// nurse's administer step — the badge is decorative, it must not change behavior
export function getSequenceActionNote(sequence: PatientTherapySequence, action?: PatientTherapySequenceEventType) {
  switch (action) {
    case PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE:
      return sequence.pharmacyNote
    case PatientTherapySequenceEventType.enum.ADMINISTER:
      return sequence.therapyNote || sequence.note
    default:
      return null
  }
}

interface SequenceActionButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** omit for non-action buttons (e.g. the note-mode check button) that never carry a badge */
  action?: PatientTherapySequenceEventType
  sequence: PatientTherapySequence
  /** null hides the tooltip — the button is enabled or the reason is self-evident */
  disabledReason?: string | null
  /** may be async: while the returned promise is pending the button shows a spinner */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>
}

// the common step-button rendering: a button (or select trigger) wrapped in a
// disabled-reason tooltip plus the amber note badge when the step carries any notes
export function SequenceActionButton({
  action,
  sequence,
  disabledReason,
  onClick,
  children,
  width,
  ...buttonProps
}: SequenceActionButtonProps) {
  const note = getSequenceActionNote(sequence, action)
  const [isPending, setIsPending] = useState(false)

  // server-backed step actions resolve asynchronously; keep the button loading until
  // they settle so it can't be pressed twice mid-request and the wait is visible
  async function handleClick(event: MouseEvent<HTMLButtonElement>) {
    const result = onClick?.(event)
    if (!result || typeof result.then !== 'function') return

    setIsPending(true)
    try {
      await result
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Tooltip content={disabledReason ?? null}>
      <Box position="relative" display="inline-block" width={width ?? undefined}>
        <Button
          width={width}
          {...buttonProps}
          loading={buttonProps.loading || isPending}
          onClick={event => {
            void handleClick(event)
          }}
        >
          {children}
        </Button>
        {note && <NoteBadge />}
      </Box>
    </Tooltip>
  )
}
